import type {
  HomeValorBannerRow,
  HomeValorBenefit,
  HomeValorContent,
} from '../models/home-valor-content.model';
import { prepareValorIntroHtml, prepareValorTitleHtml } from './home-valor-html.util';

/** Iconos fijos por orden de beneficio (1–3); no administrables (decisión B.3). */
const BENEFIT_ICONS: Record<number, string> = {
  1: 'assets/img/Home/valor/icon-benefit-time.svg',
  2: 'assets/img/Home/valor/icon-benefit-conditions.svg',
  3: 'assets/img/Home/valor/icon-benefit-simplicity.svg',
};

function normalizeOrden(row: HomeValorBannerRow, index: number): number {
  const orden = row.orden;
  return typeof orden === 'number' && !Number.isNaN(orden) ? orden : index;
}

function mapBenefitRow(
  row: HomeValorBannerRow,
  orden: number,
  fallbackBenefit: HomeValorBenefit | undefined
): HomeValorBenefit | null {
  const title = row.titulo?.trim();
  const description = row.contenido?.trim();

  if (!title && !description) {
    return fallbackBenefit ?? null;
  }

  return {
    title: title || fallbackBenefit?.title || '',
    description: description || fallbackBenefit?.description || '',
    iconSrc: BENEFIT_ICONS[orden] ?? fallbackBenefit?.iconSrc ?? BENEFIT_ICONS[1],
  };
}

/**
 * Mapeo B.3 — registros `DescripcionHome` ordenados ASC:
 * - `orden` 0 → cabecera: `titulo` eyebrow, `nombre_boton` título, `contenido` intro
 * - `orden` 1–3 → beneficios: `titulo` nombre, `contenido` descripción
 */
export function mapValorBannersToContent(
  rows: HomeValorBannerRow[],
  fallback: HomeValorContent
): HomeValorContent {
  if (!rows?.length) {
    return fallback;
  }

  const sorted = [...rows].sort(
    (a, b) => normalizeOrden(a, 0) - normalizeOrden(b, 0)
  );

  const headerRow = sorted.find((row) => normalizeOrden(row, -1) === 0) ?? sorted[0];
  const benefitRows = sorted.filter((row) => {
    const orden = normalizeOrden(row, -1);
    return orden >= 1 && orden <= 3;
  });

  const eyebrowRaw = headerRow?.titulo?.trim();
  const titleRaw = headerRow?.nombreBoton?.trim();
  const introRaw = headerRow?.contenido?.trim();

  const beneficios: HomeValorBenefit[] = [];
  for (let orden = 1; orden <= 3; orden++) {
    const row = benefitRows.find((r) => normalizeOrden(r, -1) === orden);
    const fallbackBenefit = fallback.beneficios[orden - 1];
    const mapped = row ? mapBenefitRow(row, orden, fallbackBenefit) : fallbackBenefit;
    if (mapped) {
      beneficios.push(mapped);
    }
  }

  if (!eyebrowRaw && !titleRaw && !introRaw && beneficios.length === 0) {
    return fallback;
  }

  return {
    eyebrow: eyebrowRaw || fallback.eyebrow,
    titleHtml: titleRaw ? prepareValorTitleHtml(titleRaw) : fallback.titleHtml,
    introHtml: introRaw ? prepareValorIntroHtml(introRaw) : fallback.introHtml,
    beneficios: beneficios.length ? beneficios : fallback.beneficios,
  };
}
